import React, { useEffect } from "react";

import styled from "styled-components/macro";

import { usePaymentPage } from "Payments/PaymentPage/PaymentPageContext";
import { useGetSelectedPaymentMethodDetails } from "Payments/PaymentPage/Shared/useGetSelectedPaymentMethodDetails";
import Notice from "Payments/Shared/Notice";
import { getCardTypePretty } from "Payments/Shared/getCardTypePretty";
import { shouldShowNeedsUpdating } from "Payments/Shared/shouldShowNeedsUpdating";
import classNames from "classnames";
import { motion } from "framer-motion";

import FnrIcon from "UI/FnrIcon";
import { colors } from "UI/colors";

import { usePaymentForm } from "../../PaymentFormContext";

export default function SelectedPaymentMethod({
  isHovering,
  isSkippedAuth,
  handlePaymentMethodClick,
}) {
  const {
    state: { selectedPaymentMethodID, network },
  } = usePaymentPage();
  const {
    dispatch: formDispatch,
    state: { formStages, isSubmitting },
  } = usePaymentForm();
  const paymentMethod = useGetSelectedPaymentMethodDetails();

  useEffect(
    function checkForNoSelectedPaymentMethod() {
      const amountIsComplete = formStages.amount === "complete";
      const authIsComplete =
        formStages.auth === "skipped" || formStages.auth === "isFullyLoggedIn";

      if (!selectedPaymentMethodID) {
        if (amountIsComplete && authIsComplete) {
          formDispatch({
            type: "setFormStage",
            stage: "paymentInfo",
            value: network.isACHAllowed ? "chooseAddMethodType" : "addCard",
          });
        } else {
          formDispatch({
            type: "setFormStage",
            stage: "paymentInfo",
            value: "untouched",
          });
        }
      }
    },
    [selectedPaymentMethodID, network.isACHAllowed, formStages, formDispatch]
  );

  const { nickname, last4, cardType } = paymentMethod;
  const cardTypePretty = getCardTypePretty(cardType);

  const showUpdateNotice = shouldShowNeedsUpdating(paymentMethod);

  return (
    <Wrapper>
      <MethodContent>
        <CardInfo animate={{ scale: isHovering ? 1.06 : 1 }}>
          <CardLine1 data-cy="fund-it-card-nickname">
            {nickname || cardTypePretty}{" "}
            {showUpdateNotice && <UpdateNotice>Needs updating</UpdateNotice>}
          </CardLine1>
          <CardLine2 data-cy="fund-it-card-mask">
            <Dots>••••</Dots>
            {last4}
          </CardLine2>
        </CardInfo>
        {isSkippedAuth ? (
          <TrashIcon
            isSubmitting={isSubmitting}
            onClick={handlePaymentMethodClick}
          >
            <FnrIcon fas fa-trash />
          </TrashIcon>
        ) : (
          <MoreArrow
            animate={{ scale: isHovering ? 1.06 : 1, x: isHovering ? 4 : 0 }}
            className={classNames({ isHovering })}
          >
            <FnrIcon fas fa-chevron-right />
          </MoreArrow>
        )}
      </MethodContent>
    </Wrapper>
  );
}

const Wrapper = styled(motion.div)`
  flex-grow: 1;
`;

const MethodContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CardInfo = styled(motion.div)`
  margin-right: 14px;
  transform-origin: 0 50%;
`;

const CardLine1 = styled.div`
  font-size: 18px;
  font-weight: 400;

  @media (max-width: 360px) {
    font-size: 15px;
  }
`;

const CardLine2 = styled.div`
  color: #5b5b5b;
  font-size: 16px;
  margin-top: 2px;
  display: flex;
  align-items: center;
`;

const Dots = styled.div`
  font-size: 21px;
  line-height: 21px;
  margin-right: 4px;
`;

const MoreArrow = styled(motion.div)`
  font-size: 24px;
  color: #828c8d;
  margin-right: 4px;
  transition: color 0.2s;

  &.isHovering {
    color: var(--fundColor);
  }
`;

const TrashIcon = styled(MoreArrow)`
  display: block;
  font-size: 18px;
  transition: transform 150ms, opacity 150ms;
  cursor: ${({ isSubmitting }) => (isSubmitting ? "not-allowed" : "pointer")};

  &:hover {
    transform: ${({ isSubmitting }) => (isSubmitting ? null : "scale(1.1)")};
    color: ${({ isSubmitting }) => (isSubmitting ? null : colors.fnRed)};
  }

  &:active {
    transform: ${({ isSubmitting }) => (isSubmitting ? null : "scale(0.96)")};
    color: ${({ isSubmitting }) => (isSubmitting ? null : colors.fnRed)};
  }
`;

const UpdateNotice = styled(Notice)`
  position: relative;
  border-width: 1px;
  font-size: 12px;
  display: inline-block;
  padding-block: 2px;
`;
